import dayjs from 'dayjs';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { Icon } from 'src/common/components/icon';
import { useOpenModal } from 'src/common/components/next-modal';
import { useOpenUpgradeAiModal } from 'src/components/select-upgrade-plan/upgrade-ai';
import { PRODUCT_AI_PAY_TITLE, PRODUCT_AI_TITLE, PRODUCT_AI_TITLE_BUILDIN } from 'src/const/title';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { useCurrentSpace } from 'src/hooks/space';
import { Modals } from 'src/modals';
import { useIsAiPlan, useSpaceCapacity } from 'src/services/capacity';
import { OrderListSupport, useSetting } from '../common';
import { Option } from '../option';
import { FlowusAIHistory } from './ai-history';
import { DATE_TIME_FORMAT } from 'src/common/const';
import { useApiState } from '@flowus/common/hooks/use-api-state';
import { request } from 'src/common/request';

export const SpaceInfoAI: FC = () => {
  const currentSpace = useCurrentSpace();
  const spaceCapacity = useSpaceCapacity();
  const openModal = useOpenModal();
  const isAiPlan = useIsAiPlan();
  const openUpgradeAiModal = useOpenUpgradeAiModal();
  const { enableAI } = useEnableAI();
  const { toUpgradeAi } = useSetting();
  const [chatActivityInfo] = useApiState({
    getApi: () => request.ai.getChatActivity(currentSpace.uuid),
  });

  const openAiOrderList = useCallback(() => {
    openModal.modal({
      modalId: Modals.ORDER_LIST_SUPPORT,
      content: () => (
        <OrderListSupport
          title={PRODUCT_AI_PAY_TITLE}
          subTitle={`购买${PRODUCT_AI_PAY_TITLE}`}
          orderType="aiUsage"
          openUpgradeModal={openUpgradeAiModal}
        />
      ),
    });
  }, [openModal, openUpgradeAiModal]);

  const openHistory = () => {
    openModal.modal({
      content: () => <FlowusAIHistory />,
    });
  };

  const aiPlanButton = useMemo(() => {
    if (spaceCapacity.monthlyEndDate) {
      return (
        <span className="animate-click" onClick={() => openAiOrderList()}>
          失效日期 {dayjs(spaceCapacity.monthlyEndDate).format(DATE_TIME_FORMAT)}
        </span>
      );
    }
    return null;
  }, [openAiOrderList, spaceCapacity.monthlyEndDate]);

  if (__BUILD_IN__) {
    const { gpt3_5, pdf, additional } = spaceCapacity;
    const monthUsed = gpt3_5.monthUsed + pdf.monthUsed;
    const gpt3Rest = isAiPlan
      ? gpt3_5.monthLimit - gpt3_5.monthUsed
      : gpt3_5.dayLimit - gpt3_5.todayUsed;
    const pdfRest = isAiPlan ? pdf.dayLimit - pdf.todayUsed : pdf.monthLimit - pdf.monthUsed;
    return (
      <>
        <Option title={PRODUCT_AI_TITLE_BUILDIN} />
        <div className="flex items-center text-t3 h-9">
          <span className="mr-4">本月使用量 {monthUsed}</span>
          <span>剩余使用量 {pdfRest + gpt3Rest}</span>
          <button className="ml-auto mr-[5px] flex items-center" onClick={openHistory}>
            <span className="text-t3 text-grey3">查看详情</span>
            <Icon name="IcArrowUnfold" size="xxxsmall" className="text-grey3" />
          </button>
        </div>

        <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3">
          <div className="h-7 flex justify-between items-center">
            <span className="">
              GPT-3.5 基础请求次数：{isAiPlan ? '每月' : '每日'}
              {isAiPlan
                ? `${gpt3_5.monthUsed}/${gpt3_5.monthLimit}`
                : `${gpt3_5.todayUsed}/${gpt3_5.dayLimit}`}
            </span>
          </div>
          <div className="h-7 flex justify-between items-center">
            <span>GPT-3.5 补充请求次数：总共 {additional}</span>
          </div>
          <div className="h-7 flex justify-between items-center">
            <span>
              阅读 PDF：{isAiPlan ? '每日' : '每月'}
              {isAiPlan ? `${pdf.todayUsed}/${pdf.dayLimit}` : `${pdf.monthUsed}/${pdf.monthLimit}`}
            </span>
          </div>
        </div>

        <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3 mt-2.5 flex justify-between">
          <span>AI 补充权益</span>
          <a
            className="underline ml-2 text-t3 text-active_color cursor-pointer"
            target="_blank"
            onClick={() => toUpgradeAi()}
          >
            购买查询额度
          </a>
        </div>
      </>
    );
  }

  return (
    <>
      <Option title={PRODUCT_AI_TITLE} />
      <div className="flex items-center text-t3 h-9">
        <span className="mr-4">AI 本月使用量 {spaceCapacity.usage}</span>
        <span>剩余使用量 {spaceCapacity.aiRestUsage}</span>
        {/* TODO AI */}
        <button
          hidden={!enableAI}
          className="ml-auto mr-[5px] flex items-center"
          onClick={openHistory}
        >
          <span className="text-t3 text-grey3">查看详情</span>
          <Icon name="IcArrowUnfold" size="xxxsmall" className="text-grey3" />
        </button>
      </div>

      <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3">
        <div className="h-7 flex justify-between items-center">
          <span className="">基础配额 {spaceCapacity.basic}</span>
          <span className="ml-auto">
            重置日期 {dayjs(spaceCapacity.resetDate).format(DATE_TIME_FORMAT)}
          </span>
        </div>
        {currentSpace.planType === 'team' && (
          <div className="h-7 flex justify-between items-center">
            <span className="">席位配额 {spaceCapacity.seat}</span>
            <span className="ml-auto">
              重置日期 {dayjs(spaceCapacity.resetDate).format(DATE_TIME_FORMAT)}
            </span>
          </div>
        )}

        <div className="h-7 flex justify-between items-center">
          <span>补充配额 {spaceCapacity.additional}</span>
        </div>
        {chatActivityInfo?.status && (
          <div className="h-7 flex justify-between items-center">
            <span className="text-red text-t2-medium">无限量活动</span>
            <span className="text-red ml-auto">
              截止日期 {dayjs(chatActivityInfo.endDate).format(DATE_TIME_FORMAT)}
            </span>
          </div>
        )}
      </div>

      <div className="bg-grey9 rounded py-2 px-4 text-t2 text-grey3 mt-2.5 flex justify-between">
        <div className="space-x-1">
          <span>{PRODUCT_AI_PAY_TITLE}</span>
          <span
            className="text-active_color underline underline-offset-2 animate-click"
            onClick={() => openUpgradeAiModal()}
          >
            购买查询额度
          </span>
        </div>
        {aiPlanButton}
      </div>

      {!__BUILD_IN__ && (
        <p className="text-t3 text-grey3 h-9 flex items-center">
          配额以虚拟券形式发放，不同功能消耗的虚拟券数量也不相同，
          <a
            href="/share/a3555796-d96c-4e02-9a38-4aba1ca6a418"
            className="underline"
            target="_blank"
          >
            了解详情
          </a>
        </p>
      )}
    </>
  );
};
